import { isStaging } from "app/constants/env";
import { useClaimrWidget, useTheme } from "app/shared";
import { FC, useEffect, useState } from "react";
import { useDisconnect } from "wagmi";

export interface IClaimrParams {
  userToken?: {
    token: string;
  };
  questId?: string;
  onLoaded?: () => void;
}

function observeClaimr(nodeId: string, callback: any) {
  const observer = new MutationObserver((mutations) => {
    if (window.claimr) {
      observer.disconnect();
      callback(window.claimr);
    }
  });

  observer.observe(document.getElementById(nodeId) as Node, {
    attributes: true,
    childList: true,
  });
}

const QuestClaimrWidget: FC<IClaimrParams> = ({
  userToken,
  questId,
  onLoaded,
}) => {
  const { isInitialized, setInitialized } = useClaimrWidget();
  const { theme } = useTheme();
  const { disconnect } = useDisconnect();
  const containerId = "CLAIMR_CONTAINER_ID";
  const [retryCount, setRetryCount] = useState<number>(0);

  const handleLogout = () => {
    disconnect();
  };

  useEffect(() => {
    if (!userToken) {
      return;
    }

    const loadScript = () => {
      const script = document.createElement("script");
      script.src = `https://widgets.claimr.io/claimr.min.js?rnd=${Math.random()
        .toString(36)
        .substring(2)}`;
      script.id = "claimr-script";
      script.dataset.addons =
        "sup,slb,sur,sct,wcc,lam,esc,fcm,sqp,eap,eup,pvm,slc,scd,rcc,scc,dcc";
      script.dataset.organization = "inmind";
      script.dataset.campaign = isStaging()
        ? "alphamind_staging"
        : "alphamind-onboarding";

      script.dataset.autoresize = "true";
      script.dataset.container = containerId;
      script.async = true;
      script.setAttribute("data-theme-key", "alphamind-theme");
      //@ts-ignore
      script.setAttribute("data-user-token", userToken.token.token);

      script.onload = () => {
        observeClaimr(containerId, (claimr: any) => {
          claimr.set_theme(theme);
          questId && claimr.open_contest(questId);
          onLoaded?.();
          claimr.on_logout = handleLogout;
        });
        setInitialized(true);
      };

      script.onerror = () => {
        setRetryCount((prevCount) => prevCount + 1);
      };

      document.body.appendChild(script);
    };

    if (!isInitialized) {
      loadScript();
    } else {
      if (window.claimr) {
        //@ts-ignore
        window.claimr.set_theme(theme);
        //@ts-ignore
        window.claimr.on_logout = handleLogout;
      }
    }
  }, [userToken, isInitialized, setInitialized, theme, retryCount, questId]);

  useEffect(() => {
    if (retryCount > 0 && retryCount < 3) {
      const retryTimer = setTimeout(() => {
        const scriptElement = document.getElementById("claimr-script");
        if (scriptElement) {
          scriptElement.remove();
        }
        setInitialized(false);
      }, 3000);

      return () => clearTimeout(retryTimer);
    }
  }, [retryCount, setInitialized]);

  useEffect(() => {
    localStorage.setItem("alphamind-theme", theme);
    if (isInitialized && window.claimr) {
      //@ts-ignore
      window.claimr.set_theme(theme);
    }
  }, [theme, isInitialized]);

  // Fix claimr blocking page scroll
  useEffect(() => {
    if (!isInitialized) return;

    // Force body scroll to remain enabled
    const forceBodyScroll = () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';

      // Remove pointer-events: none from body if claimr adds it
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = 'auto';
      }
    };

    // Find and fix claimr overlay elements that might block scrolling
    const fixClaimrOverlays = () => {
      // Find all claimr-related divs
      const container = document.getElementById(containerId);
      if (!container) return;

      // Look for overlay/backdrop elements within claimr
      const allDivs = container.querySelectorAll('div');
      allDivs.forEach((div) => {
        const computed = window.getComputedStyle(div);

        // If div is positioned fixed/absolute and covers large area, it might be an overlay
        if ((computed.position === 'fixed' || computed.position === 'absolute')) {
          const rect = div.getBoundingClientRect();
          const isLargeOverlay = rect.width > window.innerWidth * 0.8 ||
                                 rect.height > window.innerHeight * 0.8;

          // Check if it's blocking the entire page
          if (isLargeOverlay && computed.zIndex && parseInt(computed.zIndex) > 100) {
            // This looks like an overlay - disable pointer events so page can scroll
            (div as HTMLElement).style.pointerEvents = 'none';
          }
        }
      });
    };

    // MutationObserver to detect when claimr tries to lock scroll
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' &&
            (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
          const target = mutation.target as HTMLElement;
          if (target === document.body || target === document.documentElement) {
            forceBodyScroll();
          }
        }

        // Check for new nodes that might be overlays
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          setTimeout(fixClaimrOverlays, 50);
        }
      });
    });

    // Observe body and html for style/class changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Also force scroll on initial load
    forceBodyScroll();
    fixClaimrOverlays();

    // Check periodically in case claimr overrides
    const intervalId = setInterval(() => {
      forceBodyScroll();
      fixClaimrOverlays();
    }, 500);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, [isInitialized, containerId]);

  return (
    <div
      id={containerId}
      style={{ width: "100%" }}
      data-theme-key="alphamind-theme"
    ></div>
  );
};

export default QuestClaimrWidget;
